import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Copy, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { RedactedText } from '@/components/redacted-email';
import { useSession } from '@/contexts/session-context';
import { useWorkspace } from '@/contexts/workspace-context';
import { useFetch } from '@/hooks/use-fetch';
import { useApiMutation } from '@/hooks/use-api-query';
import { getOsCopyShortcut } from '@/lib/platform';

const DISMISS_ERROR_MESSAGE =
  "Couldn't save your preference. It may show again on reload.";

interface MintResponse {
  code: string;
  expiresAt: number;
  instructionBlock: string;
  warningCopy?: string;
}

/**
 * First-login welcome modal. Wraps the same server-rendered instruction
 * block that BootstrapCodeDialog (on Quick Start) shows, so the user can
 * paste install + login instructions into their AI agent without leaving
 * this modal. We intentionally do NOT show the instruction text upfront —
 * the block contains a one-time login code, mirroring the reveal-on-copy
 * pattern of BootstrapCodeDialog.
 *
 * Mounted in DashboardLayout. Opens once per user when both `cliLastSeenAt`
 * and `welcomeModalDismissedAt` are null. Dismiss POST is awaited and
 * followed by refresh() so the in-memory user reflects the new timestamp;
 * failures surface via toast instead of being swallowed.
 */
export function TeammateWelcomeModal() {
  const { user, role, refresh } = useSession();
  const { activeWorkspace } = useWorkspace();
  const fetchWithAuth = useFetch();
  const [open, setOpen] = useState(false);
  const [mint, setMint] = useState<MintResponse | null>(null);
  const [mintError, setMintError] = useState<string | null>(null);
  const [minting, setMinting] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);
  const triggeredRef = useRef(false);
  const fallbackRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (!user) return;
    // Mint requires X-Workspace-Id (WorkspaceGuard). Wait for activeWorkspace
    // to load before opening — otherwise the first mint POST 403s and the
    // user sees an empty modal with a generic error.
    if (!activeWorkspace) return;
    // Only show to invited teammates. Org creators (and admins promoted at
    // create-time) get role === 'admin' and don't need the "you've been
    // invited" framing — they set the workspace up themselves and can find
    // the same install snippet on Quick Start.
    if (role !== 'member') return;
    if (user.cliLastSeenAt !== null) return;
    if (user.welcomeModalDismissedAt !== null) return;
    if (triggeredRef.current) return;
    triggeredRef.current = true;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setOpen(true);
  }, [user, role, activeWorkspace]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const controller = new AbortController();
    setMint(null);
    setMintError(null);
    setCopyFailed(false);
    setMinting(true);
    void (async () => {
      try {
        const res = await fetchWithAuth('/auth/bootstrap/mint', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ variant: 'dashboard' }),
          signal: controller.signal,
        });
        if (cancelled) return;
        if (!res.ok) {
          setMintError(
            "Couldn't generate your install instructions. Try again later from Quick Start.",
          );
          return;
        }
        setMint((await res.json()) as MintResponse);
      } catch (err) {
        if ((err as { name?: string })?.name === 'AbortError') return;
        if (!cancelled) {
          setMintError(
            "Couldn't generate your install instructions. Try again later from Quick Start.",
          );
        }
      } finally {
        if (!cancelled) setMinting(false);
      }
    })();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [open, fetchWithAuth]);

  useEffect(() => {
    if (copyFailed) {
      setTimeout(() => fallbackRef.current?.select(), 0);
    }
  }, [copyFailed]);

  // Dismiss is awaited server-side and followed by refresh() so the
  // in-memory user reflects the new timestamp; failures surface via toast
  // instead of being swallowed. The fixed copy is deliberate — a backend
  // message adds nothing to "your preference didn't save".
  const dismissWelcome = useApiMutation<void, void>(
    () => ({
      path: '/auth/welcome-modal/dismiss',
      init: { method: 'POST' },
      fallback: DISMISS_ERROR_MESSAGE,
    }),
    {
      onSuccess: () => refresh(),
      onError: () => toast.error(DISMISS_ERROR_MESSAGE),
    },
  );

  function handleClose() {
    setOpen(false);
    dismissWelcome.mutate();
  }

  async function handleCopy() {
    if (!mint) return;
    try {
      await navigator.clipboard.writeText(mint.instructionBlock);
      toast.success('Instructions copied. Paste into your AI agent.');
      setOpen(false);
      dismissWelcome.mutate();
    } catch {
      setCopyFailed(true);
    }
  }

  const workspaceName = activeWorkspace?.name ?? 'HookMyApp';
  const shortcut = getOsCopyShortcut();
  const heading = copyFailed
    ? `Copy failed. Press ${shortcut} to copy manually.`
    : `You've Joined ${workspaceName}`;

  return (
    <Dialog open={open} onOpenChange={(next) => (!next ? handleClose() : null)}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{heading}</DialogTitle>
          {!copyFailed && (
            <DialogDescription>
              HookMyApp lives inside your AI coding agent, whether that's
              Claude Code, Cursor, or whatever you use. For it to work, your
              agent needs the HookMyApp CLI plus a skill that teaches it how
              everything fits together. Copy the snippet below and paste it
              into your agent. It installs both and signs you in.
            </DialogDescription>
          )}
        </DialogHeader>
        {!copyFailed && (mint?.warningCopy || mintError) && (
          <p className="rounded-md border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
            <RedactedText text={mintError ?? mint?.warningCopy ?? ''} />
          </p>
        )}
        {copyFailed && mint && (
          <textarea
            ref={fallbackRef}
            readOnly
            value={mint.instructionBlock}
            aria-label="Install instructions"
            className="h-48 w-full rounded-md border bg-muted px-3 py-2 font-mono text-xs"
          />
        )}
        <DialogFooter className="gap-2">
          {copyFailed ? (
            <Button onClick={handleClose}>Done</Button>
          ) : (
            <>
              <Button variant="ghost" onClick={handleClose} disabled={minting}>
                Dismiss
              </Button>
              <Button
                onClick={() => void handleCopy()}
                disabled={minting || mint === null || mintError !== null}
              >
                {minting ? (
                  <Loader2 className="size-3.5 animate-spin" />
                ) : (
                  <Copy className="size-3.5" />
                )}
                <span className="ml-1.5">Copy Install Instructions</span>
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
